// Browser error tracking. Inert until REACT_APP_SENTRY_DSN is set, so this ships safely to
// every environment including production before you have an account.
//
// Without this, a React crash showed the user the ErrorBoundary recovery screen and left no
// trace anywhere - the only signal was a customer bothering to tell you. Now the stack, the
// route and the environment arrive somewhere you can see them.
//
// The SDK is loaded with a DYNAMIC import, on purpose. A static `import * as Sentry` pulls
// ~28 kB gzipped into the main bundle whether or not a DSN is configured - every visitor
// paying a page-load cost for a feature that may be switched off. With the dynamic form,
// webpack emits it as a separate chunk that is only ever fetched when a DSN exists.
//
// CRA inlines REACT_APP_* at build time, so this is fixed per deploy: each Netlify site
// carries its own DSN and environment.

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = (process.env.REACT_APP_APP_ENV || 'prod').toLowerCase();

let sentry = null;   // populated once the chunk resolves
let starting = false;

export function initErrorReporting() {
  if (!DSN || starting) return;
  starting = true;

  import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn: DSN,
        environment: ENV,
        // Only a sample of traces - errors are always captured in full. Keeps the bill sane.
        tracesSampleRate: ENV === 'prod' ? 0.1 : 1.0,
        // Session replay is deliberately NOT enabled: this app renders real prospects' names,
        // emails and phone numbers, and replays would ship that to a third party. Microsoft
        // Clarity already covers behavioural analytics on production.
        beforeSend(event) {
          // Belt and braces - never let a contact's details ride along in a URL or form field.
          if (event.request) {
            delete event.request.data;
            delete event.request.cookies;
          }
          return event;
        },
      });
      sentry = Sentry;
    })
    .catch(() => {
      /* error reporting must never break the app - if the chunk fails to load, carry on */
    });
}

/** Tie errors to the signed-in user by id only - never email, which is customer data. */
export function identifyForErrors(user) {
  if (!sentry || !user) return;
  try {
    sentry.setUser({ id: user.id });
  } catch {
    /* no-op */
  }
}

/**
 * Manually report a caught error. No-op when reporting is off or the SDK chunk hasn't
 * finished loading - a crash in the first few hundred milliseconds still reaches the
 * console and the ErrorBoundary, which is the trade for not shipping 28 kB to everyone.
 */
export function reportError(error, context = {}) {
  if (!sentry) return;
  try {
    sentry.withScope((scope) => {
      Object.entries(context).forEach(([k, v]) => scope.setTag(k, String(v)));
      sentry.captureException(error);
    });
  } catch {
    /* no-op */
  }
}
